import { fromJS } from "immutable";
import { buildLocalSmartfocus, randomString } from "../containers/timeline/timeline-helper";

/**
 * Dynamically checks if there is enough space in zoomData for a new entry.
 *
 * @param {number} playhead - Current playhead time
 * @param {object} zoomData - Nested zoom data object
 * @param {number} [minGap=1] - Minimum required gap (default = 1)
 * @returns {{status: boolean, message: string, before?: object, after?: object}}
 */
/**
/**
* Check if there's space to insert a new zoom that STARTS at `playhead`
* and lasts `minGap` seconds (i.e. occupies [playhead, playhead + minGap]).
*
* @param {number} playhead - time where new zoom would start
* @param {object} zoomData - nested zoom data
* @param {number} [minGap=1] - duration (seconds) of the new zoom
* @returns {{
*   status: boolean,
*   message: string,
*   before?: {start:number,end:number},
*   after?: {start:number,end:number},
*   suggestedStart?: number
* }}
*/

export const checkZoomSpace = (playhead, zoomData, item, minGap = 1) => {
  // Existing logic for extracting zoom pairs
  const extractZoomPairs = (obj) => {
    if (!obj) return [];

    return Object.values(obj).flatMap(group => {
      const stages = Object.values(group || []);
      const zoomIn = stages.find(s => s?.type === "zoomIn");
      const zoomOut = stages.find(s => s?.type === "zoomOut");

      if (!zoomIn || !zoomOut) return [];
      const enterDuration = zoomIn.start - item.get("videoStart");
      const exitDuration = zoomOut.end - item.get("videoStart");
      return [{
        start: enterDuration + item.get("enterStart"),
        end: exitDuration + item.get("enterStart"),
      }];
    });
  };

  const zoomPairs = extractZoomPairs(zoomData).sort((a, b) => a.start - b.start);

  // New: Check if there's enough time remaining in the video
  const videoEnd = item.get("exitEnd") || item.get("playEnd");
  if (playhead >= videoEnd || (videoEnd - playhead) < minGap) {
    return {
      status: false,
      message: "Not enough time remaining in the video for this effect"
    };
  }

  // Existing overlap check
  const isInside = zoomPairs.find(({ start, end }) =>
    playhead >= start && playhead <= end
  );

  if (isInside) {
    const after = zoomPairs.find(r => r.start >= playhead) || null;
    const currentZoom = zoomPairs.find(zoom =>
      playhead >= zoom.start && playhead <= zoom.end
    );

    if (after && currentZoom) {
      const availableSpace = after.start - currentZoom.end;
      if (availableSpace >= minGap) {
        return {
          status: true,
          before: currentZoom,
          after,
          suggestedStart: currentZoom.end - item.get("enterStart"),
          nextSlider: true
        };
      }
    }

    // If no space after current zoom, find the next available slot
    const nextAvailableStart = after ? after.end + 1 : currentZoom ? currentZoom.end + 1 : playhead;

    return {
      status: true,
      before: currentZoom,
      after: after || null,
      suggestedStart: nextAvailableStart,
      nextSlider: true
    };
  }

  // Rest of the existing logic remains the same
  const before = [...zoomPairs].reverse().find(r => r.end <= playhead) || null;
  const after = zoomPairs.find(r => r.start >= playhead) || null;
  const newZoomEnd = playhead + minGap;

  // New: Ensure new zoom doesn't exceed video duration
  if (newZoomEnd > videoEnd) {
    return {
      status: false,
      message: "Not enough time remaining in the video for this effect"
    };
  }

  const hasSpaceAfter = !after || after.start >= newZoomEnd;
  const hasSpaceBefore = !before || before.end <= playhead;

  if (hasSpaceBefore && hasSpaceAfter) {
    return {
      status: true,
      message: "Enough space available for new zoom",
      before,
      after
    };
  }

  // Existing candidate suggestion logic
  const candidates = [];
  if (before) candidates.push(before.end);
  if (after) candidates.push(after.start - minGap);

  const suggestedStart = candidates
    .filter(c => c >= 0)
    .find(c => {
      const cEnd = c + minGap;
      return !zoomPairs.some(r => !(r.end <= c || r.start >= cEnd));
    });

  return {
    status: false,
    message: "Not enough space to insert zoom at current position",
    before,
    after,
    suggestedStart: suggestedStart !== undefined ? suggestedStart : null
  };
};

export function findMatchingZoomOut(zoomData, startTime) {
  const entry = Object.entries(zoomData).find(([, zoomGroup]) =>
    Object.values(zoomGroup).some(
      stage => stage?.type === "zoomOut" && stage?.end === startTime
    )
  );
  return entry ? entry[0] : null;
}

/**
 * Common function to handle saving zoom data.
 *
 * @param {object} params - Parameters for the function
 * @param {object} params.item - The selected item from the workspace
 * @param {number} params.playhead - The current playhead time
 * @param {object} params.project - The project details
 * @param {function} params.dispatch - Redux dispatch function
 * @param {function} params.setIsSpaceUnavailable - State setter for space availability
 */
export const applyZoomToTimeline = async ({
  item,
  playhead,
  project,
  updateTimelineTime,
  updateProjectData,
  setIsSpaceUnavailable,
  setSelectedZoomId,
  setSelectedItems, }) => {
  if (!item || playhead === undefined) return;

  const zoomData = item.get("zoomData")?.toJS() || {};

  // Check available space with maximum possible duration (3 seconds)
  const maxDuration = 1; // Maximum allowed duration
  const result = checkZoomSpace(playhead, zoomData, item, maxDuration);

  // Get item timing properties to convert timeline time to media-relative time
  const enterStart = item.get("enterStart");
  const videoStart = item.get("videoStart") || 0;
  const newZoomId = `${randomString("zoom")}`;
  let duration = result.after
    ? result.after.start - playhead
    : item.get("videoEnd") - (playhead - enterStart);

  // Convert timeline playhead to media-relative time
  let mediaRelativePlayhead = playhead - enterStart + videoStart;

  let disableZoomOut = false;
  let disableZoomIn = false;
  let beforeElementId = null;

  if (result.nextSlider) {
    const afterStart = result?.after?.start;
    const beforeEnd = result?.before?.end;

    if (typeof afterStart !== "number" && typeof beforeEnd !== "number") {
      return;
    }

    const dur = afterStart - beforeEnd;
    mediaRelativePlayhead = result.suggestedStart
    if (!afterStart && beforeEnd) {
      mediaRelativePlayhead = result.suggestedStart - enterStart + videoStart;
    }
    duration = !result.after ? 3 : dur > 3 ? 3 : dur;

    const beforeEndWithoutTimeline = beforeEnd - enterStart + videoStart;

    if (beforeEndWithoutTimeline === mediaRelativePlayhead) {
      disableZoomOut = true;
      disableZoomIn = true;
      beforeElementId = findMatchingZoomOut(zoomData, mediaRelativePlayhead);
    }
  }
  const clampedDuration = Math.max(0.5, Math.min(duration, 3));

  if (!result.status || (clampedDuration < 1 && result.status) || (result.suggestedStart > item.get("videoEnd"))) {
    setIsSpaceUnavailable(true);
    return;
  }
  const defaultX = project.get("height") > project.get("width") ? 118.63636363636363 : 202.89811643835617;
  const defaultY = project.get("height") > project.get("width") ? 210.52631578947367 : 127.89473684210526
  const newZoomOutEnd = mediaRelativePlayhead + clampedDuration
  // Check if new zoomOut end matches any existing zoomIn start
  Object.entries(zoomData).forEach(([key, group]) => {
    const { zoomIn } = group;
    if (zoomIn && zoomIn.start === newZoomOutEnd) {
      disableZoomOut = true;
      zoomData[key].zoomOut.disableZoomIn = true; // Set disableZoomIn for the zoomOut type
    }
  });

  const newZoomData = {
    zoomIn: {
      start: mediaRelativePlayhead,
      end: mediaRelativePlayhead + 0.5,
      height: project.get("height") * 0.5,  // 50% height
      width: project.get("width") * 0.5, // 50% width
      x: defaultX,
      y: defaultY,
      type: "zoomIn",
      disableZoomIn
    },
    zoomOut: {
      start: (mediaRelativePlayhead + clampedDuration) - 0.5,
      end: newZoomOutEnd,
      height: project.get("height"),
      width: project.get("width"),
      x: 0,
      y: 0,
      type: "zoomOut",
      disableZoomOut,
    },
  };

  const updatedZoomData = { ...zoomData, [newZoomId]: newZoomData };

  if (beforeElementId && disableZoomOut && disableZoomIn) {
    updatedZoomData[beforeElementId].zoomOut.disableZoomOut = true;
  }

  // Update zoomData inside workspaceItems
  const updatedProject = project.setIn(
    ["workspaceItems", item.get("id"), "zoomData"],
    fromJS(updatedZoomData)
  );

  const toUpdate = [
    {
      id: item.get("id"),
      container: "workspaceItems",
      toUpdate: {
        zoomData: updatedZoomData,
      },
    },
  ];

  if (toUpdate.length) {
    setSelectedItems({
      selectedSubtitles: [`${item.get("id")}-${newZoomId}`],
      propWindowType: "SMART_FOCUS"
    })
    setSelectedZoomId(`${item.get("id")}-${newZoomId}`)
    updateTimelineTime({ toUpdate })
    const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
    const updatedProjects = updatedLocalZoom.getIn(["localZoom"]);
    updateProjectData({ localZoom: updatedProjects });
  }
};